import { useEffect, useState } from "react";
import { Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";

const STATUS: Record<string, string> = { open: "Waiting to be claimed", claimed: "Claimed", expired: "Link expired" };

export function SharePlus() {
  const [d, setD] = useState<any>(null);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/plus/passes").then((r) => setD(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!d?.can_gift) return null;
  const copy = async (l: string) => { try { await navigator.clipboard.writeText(l); toast.success("Gift link copied"); } catch { toast.error("Couldn't copy"); } };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/plus/passes", { email, message });
      setLink(data.link); setEmail(""); setMessage(""); load();
      toast.success(data.emailed ? `Gift sent to ${data.to_email}` : "Gift ready. Copy the link and send it to them");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="mt-10 overflow-hidden rounded-3xl bg-gradient-to-br from-[#FBE7F0] via-[#FFF5EE] to-white p-6 ring-1 ring-[#F2D6DF] sm:p-8" data-testid="share-plus">
      <h2 className="flex items-center gap-2 font-display text-3xl font-semibold text-slate-900"><Gift className="h-6 w-6 text-[#E07A5F]" />Share Cast Plus</h2>
      <p className="mt-1 max-w-xl text-slate-600">Send a friend or grandparent a free 30-day guest pass to Deep Dive and reading plans. You get one each year.</p>
      {d.available ? (
        <div className="mt-6 grid max-w-xl gap-3">
          <Input data-testid="share-plus-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Their email" className="bg-white" />
          <Textarea data-testid="share-plus-message" value={message} maxLength={300} onChange={(e) => setMessage(e.target.value)} placeholder="Add a few words (optional)" className="min-h-[80px] bg-white" />
          <Button data-testid="share-plus-send" disabled={busy || !email.includes("@")} onClick={send} className="w-fit rounded-full bg-[#E07A5F] px-6 hover:bg-[#c9664c]">{busy ? "Sending…" : "Send 30-day pass"}</Button>
        </div>
      ) : <p className="mt-4 text-sm font-semibold text-slate-700" data-testid="share-plus-used">You've sent this year's pass. Your next one is ready on {new Date(d.next_at).toLocaleDateString()}.</p>}
      {link && <button data-testid="share-plus-copy-new" onClick={() => copy(link)} className="mt-4 flex max-w-xl items-center gap-2 truncate rounded-xl bg-white px-4 py-2.5 text-left text-sm text-slate-700 ring-1 ring-[#E2DBCE]"><Copy className="h-4 w-4 shrink-0" /><span className="truncate">{link}</span></button>}
      {d.sent.length > 0 && (
        <div className="mt-6 space-y-2" data-testid="share-plus-sent">
          {d.sent.map((p: any) => (
            <div key={p.code} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/80 px-4 py-3 text-sm" data-testid={`share-plus-pass-${p.code}`}>
              <span><span className="font-semibold text-slate-800">{p.to_email}</span> · <span className="text-slate-500">{STATUS[p.status]}</span></span>
              {p.status === "open" && <button data-testid={`share-plus-copy-${p.code}`} onClick={() => copy(`${window.location.origin}/pass/${p.code}`)} className="flex items-center gap-1 font-semibold text-[#C86D51]"><Copy className="h-3.5 w-3.5" />Copy link</button>}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
